import React, { useState, useEffect } from 'react';
import { Card, Table, Space, Typography, Input, Button, message, Drawer } from 'antd';
import { useNavigate } from 'react-router-dom';
import { DollarOutlined, SearchOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../services/api';
import { useBreakpoint } from '../hooks/useBreakpoint';

const { Title, Text, Paragraph } = Typography;

interface PayableRow {
  id: number;
  accountCode: string;
  accountName: string;
  phone?: string;
  balance: number;
  lastPaymentDate?: string | null;
}

const SupplierPayables: React.FC = () => {
  const isMobile = useBreakpoint();
  const navigate = useNavigate();
  const [rows, setRows] = useState<PayableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [ledgerDrawerVisible, setLedgerDrawerVisible] = useState(false);
  const [ledgerData, setLedgerData] = useState<{ accountName: string; balance: number; entries: any[] } | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const openLedgerDrawer = async (accountId: number) => {
    setLedgerDrawerVisible(true);
    setLedgerLoading(true);
    try {
      const { data } = await api.get<{ accountId: number; accountName: string; entries: any[]; balance: number }>(
        `/accounts/${accountId}/ledger`
      );
      setLedgerData({
        accountName: data.accountName,
        balance: data.balance,
        entries: data.entries || [],
      });
    } catch {
      message.error('Cari hareketleri yüklenirken hata');
    } finally {
      setLedgerLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (name.trim()) params.name = name.trim();

      const { data } = await api.get<PayableRow[]>('/accounts/payables', { params });
      setRows(data);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Tedarikçi borçları yüklenirken hata');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { title: 'Cari Kodu', dataIndex: 'accountCode', key: 'accountCode', width: 110 },
    { title: 'Tedarikçi Adı', dataIndex: 'accountName', key: 'accountName' },
    { title: 'Telefon', dataIndex: 'phone', key: 'phone', width: 180 },
    {
      title: 'Borç Bakiye (₺)',
      dataIndex: 'balance',
      key: 'balance',
      width: 140,
      render: (v: number) => (v ? Math.abs(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '-'),
    },
    {
      title: 'Son Ödeme Tarihi',
      dataIndex: 'lastPaymentDate',
      key: 'lastPaymentDate',
      width: 150,
      render: (d: string | null) => (d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '-'),
    },
    {
      title: 'İşlem',
      key: 'action',
      width: 200,
      render: (_: unknown, row: PayableRow) => (
        <Space size="small" wrap>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => openLedgerDrawer(row.id)}
            title="Ödeme / hareket geçmişi"
          />
          <Button
            type="primary"
            size="small"
            icon={<DollarOutlined />}
            onClick={() => navigate(`/payment?accountId=${row.id}&mode=payable`)}
          >
            Ödeme Yap
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Borçlar (Tedarikçi)
          </Title>
          <Space wrap>
            <Button onClick={() => navigate('/customerdept')}>
              Alacaklar (Müşteri)
            </Button>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Tedarikçi adı / Cari adı"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: 220 }}
            />
            <Button type="primary" onClick={loadData} loading={loading}>
              Listele
            </Button>
          </Space>
        </Space>
        <Text type="secondary">
          Tedarikçi veya &quot;İkisi de&quot; tipi carilerden, ödenecek bakiyesi (borç) olanlar listelenir.
        </Text>
        <Table
          rowKey="id"
          style={{ marginTop: 16 }}
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 600 }}
          pagination={{ pageSize: 20, showSizeChanger: !isMobile, showTotal: (t) => `Toplam ${t} kayıt` }}
        />
      </Card>

      <Drawer
        title="Cari hareketleri"
        placement="right"
        width={isMobile ? '100%' : 640}
        open={ledgerDrawerVisible}
        onClose={() => setLedgerDrawerVisible(false)}
      >
        {ledgerLoading && <div>Yükleniyor...</div>}
        {!ledgerLoading && ledgerData && (
          <>
            <Paragraph strong>{ledgerData.accountName}</Paragraph>
            <Paragraph>
              Cari bakiye:{' '}
              <Text type={ledgerData.balance >= 0 ? 'danger' : 'success'}>{ledgerData.balance.toFixed(2)} ₺</Text>
            </Paragraph>
            <Table
              size="small"
              dataSource={ledgerData.entries}
              rowKey="id"
              scroll={{ x: 400 }}
              columns={[
                {
                  title: 'Tarih',
                  dataIndex: 'entryDate',
                  key: 'entryDate',
                  render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm'),
                  width: 120,
                },
                {
                  title: 'Tutar',
                  dataIndex: 'amount',
                  key: 'amount',
                  render: (a: number) => `${a >= 0 ? '+' : ''}${a.toFixed(2)} ₺`,
                  width: 100,
                },
                { title: 'Açıklama', dataIndex: 'description', key: 'description' },
                { title: 'Makbuz no', dataIndex: 'receiptNo', key: 'receiptNo', width: 100 },
              ]}
              pagination={{ pageSize: 15 }}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default SupplierPayables;
